import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useCopagoArticleImport from '../../hooks/useCopagoArticleImport';

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file, 'utf-8');
  });
}

// CSV import dialog for the Copago article master export. The export is a
// headerless, semicolon-delimited file. The dialog uploads the raw text, runs a
// dry-run preview, and only writes after the user confirms.
export default function CopagoArticleImportDialog({ open, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const { isLoading, importArticles } = useCopagoArticleImport();

  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [deactivateMissing, setDeactivateMissing] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setCsv('');
    setFileName('');
    setDeactivateMissing(true);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    reset();
    onClose();
  }, [isLoading, onClose, reset]);

  const handleFileChange = useCallback(async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      setCsv(String(text || ''));
      setFileName(file.name);
      setPreview(null);
      setResult(null);
      toast.success('CSV-Datei gelesen');
    } catch (err) {
      setCsv('');
      setFileName('');
      toast.error('CSV konnte nicht gelesen werden');
    }
  }, []);

  const runPreview = useCallback(
    async nextDeactivateMissing => {
      if (!csv.trim()) {
        toast.info('Bitte zuerst eine CSV-Datei auswählen');
        return;
      }
      const response = await importArticles({
        csv,
        deactivateMissing: nextDeactivateMissing,
        dryRun: true,
      });
      if (response) setPreview(response);
    },
    [csv, importArticles]
  );

  const runImport = useCallback(async () => {
    if (!csv.trim()) {
      toast.info('Bitte zuerst eine CSV-Datei auswählen');
      return;
    }
    const response = await importArticles({ csv, deactivateMissing, dryRun: false });
    if (!response) return;
    setResult(response);
    const s = response.summary || {};
    toast.success(
      `${s.created || 0} angelegt, ${s.updated || 0} aktualisiert` +
        (s.deactivated ? `, ${s.deactivated} deaktiviert` : '')
    );
    if (onImported) onImported();
  }, [csv, deactivateMissing, importArticles, onImported]);

  const summary = preview?.summary;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Copago Artikel importieren</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            Importiert den Copago-Artikelexport (semikolon-getrennt, ohne Kopfzeile). Bestehende
            Artikel werden anhand der Copago Artikel ID überschrieben; neue Artikel werden angelegt.
            Manuell gepflegte Preise und Infotexte bleiben erhalten.
          </Typography>

          <Box>
            <Button variant='outlined' component='label' disabled={isLoading}>
              CSV auswählen
              <input
                ref={fileInputRef}
                hidden
                type='file'
                accept='.csv,.txt,text/csv,text/plain'
                onChange={handleFileChange}
              />
            </Button>
            {fileName ? (
              <Typography variant='caption' color='text.secondary' sx={{ ml: 2 }}>
                {fileName}
              </Typography>
            ) : null}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={deactivateMissing}
                disabled={isLoading}
                onChange={event => {
                  const next = event.target.checked;
                  setDeactivateMissing(next);
                  if (preview) runPreview(next);
                }}
              />
            }
            label='Artikel, die nicht in der CSV stehen, auf inaktiv setzen'
          />

          {!preview && !result ? (
            <Button
              variant='contained'
              onClick={() => runPreview(deactivateMissing)}
              disabled={isLoading || !csv.trim()}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Vorschau prüfen'}
            </Button>
          ) : null}

          {summary && !result ? (
            <Alert severity='info'>
              <Typography variant='body2'>
                {summary.rows} Zeilen gelesen · {summary.willCreate} neu · {summary.willUpdate}{' '}
                aktualisiert
                {deactivateMissing ? ` · ${summary.willDeactivate} werden deaktiviert` : ''}
                {summary.skipped ? ` · ${summary.skipped} übersprungen` : ''}
              </Typography>
            </Alert>
          ) : null}

          {preview?.unknownGroups?.length ? (
            <Alert severity='warning'>
              Unbekannte Warengruppen (werden ohne Verknüpfung importiert):{' '}
              {preview.unknownGroups.join(', ')}
            </Alert>
          ) : null}

          {preview?.skipped?.length ? (
            <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Zeile</TableCell>
                    <TableCell>Copago ID</TableCell>
                    <TableCell>Grund</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.skipped.slice(0, 200).map(item => (
                    <TableRow key={`${item.line}-${item.copagoId ?? ''}`}>
                      <TableCell>{item.line}</TableCell>
                      <TableCell>{item.copagoId ?? '—'}</TableCell>
                      <TableCell>{item.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}

          {result ? (
            <Alert severity='success'>
              <Typography variant='body2'>
                Import abgeschlossen: {result.summary.created} angelegt,{' '}
                {result.summary.updated} aktualisiert
                {result.summary.deactivated ? `, ${result.summary.deactivated} deaktiviert` : ''}
                {result.summary.skipped ? `, ${result.summary.skipped} übersprungen` : ''}.
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {result ? 'Schließen' : 'Abbrechen'}
        </Button>
        {preview && !result ? (
          <Button variant='contained' color='success' onClick={runImport} disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : 'Jetzt importieren'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
